import React, { useEffect, useRef, useState } from 'react';
import { Plus, PenLine, Pencil, Send, Check, Trash2, ArrowRight } from 'lucide-react';
import CreatePlanForm from './elements/createPlanForm.jsx';
import CreatePlanEditor from './createPlan.jsx';
import FilmBoxes from './elements/filmBoxes.jsx';
import AuthModal from './elements/authModal.jsx';
import { publishDesign, preparedFilm, prepareListingOnly } from './elements/publishDesign.jsx';
import { loadDrafts, saveDraft, deleteDraft, newDraftId } from './elements/draftStore.jsx';
import './planDrafts.scss';

const fa = (n, d = 0) => Number(n).toLocaleString('fa-IR', { maximumFractionDigits: d });

// اطلاعاتِ «سایتِ» ملک برای سه‌بعدی (نمای بیرون و منظره‌ی پشتِ پنجره‌ها): لوکیشنِ دقیق، طبقه، تعدادِ طبقات و
// جهتِ ملک — همه از فرمِ «ثبت فایل جدید». اگر فرم قدیمی باشد و این‌ها را نداشته باشد، null
function siteFromForm(formData) {
  if (!formData?.site) return null;
  return {
    lat: formData.site.lat,
    lng: formData.site.lng,
    floor: Math.max(0, Number(formData.floor) || 0),
    totalFloors: Math.max(1, Number(formData.totalFloors) || 1),
    orientation: formData.orientation || 'north',
  };
}

// dream: «خانه‌ی رویایی من» برای کاربرِ عادی — بدونِ فرمِ آگهی: مستقیم به طراحیِ دوبعدی، بعد مثلِ مشاور رندر و
// «انتشار» (بدونِ اطلاعاتِ فروش/اجاره — در اکسپلور زیرِ «همه‌ی طراحی‌ها» دیده می‌شود، نه «خانه‌های واقعی»).
// پیش‌نویس‌های این دو حالت از هم جدا نشان داده می‌شوند. onClose: دکمه‌ی برگشت (فقط در حالتِ dream)
const DREAM_TITLE = 'خانه‌ی رویایی من';

function PlanDraftsPage({ dream = false, onClose = null }) {
  const [allDrafts, setDrafts] = useState([]);
  const drafts = allDrafts.filter((d) => Boolean(d.formData && d.formData.dream) === dream);
  const [loaded, setLoaded] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingDraftId, setEditingDraftId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // draft: { id, formData, status: 'draft' | 'designed' | 'publishing' | 'published',
  //          prepared (فایل‌ها و فیلمِ ساخته‌شده با «پایان طراحی» — هنوز روی سرور نیست),
  //          editorState (پلانِ دوبعدی — نیمه‌کار هم خودکار ذخیره می‌شود)، filmUrl، progress، error }
  // همه روی همین گوشی می‌مانند (draftStore) — با عوض کردنِ تب یا بستنِ برنامه از بین نمی‌روند

  // بارِ اول: فایل‌های ذخیره‌شده
  useEffect(() => {
    let alive = true;
    loadDrafts().then((list) => {
      if (!alive) return;
      setDrafts((prev) => {
        const have = new Set(prev.map((d) => d.id));
        const loaded = list
          .filter((d) => !have.has(d.id))
          .map((d) => {
            const film = d.prepared ? preparedFilm(d.prepared) : null;
            // آدرسِ موقتِ عکس‌های آگهی با بستنِ برنامه از بین رفته — از خودِ فایل دوباره ساخته می‌شود
            const photos = ((d.formData && d.formData.photos) || []).map((ph) =>
              ph && ph.file ? { ...ph, url: URL.createObjectURL(ph.file) } : ph
            );
            return { ...d, formData: { ...d.formData, photos }, filmUrl: film ? URL.createObjectURL(film) : null };
          });
        return [...prev, ...loaded];
      });
      setLoaded(true);
    });
    return () => {
      alive = false;
    };
  }, []);

  const handleCreate = (formData) => {
    const draft = { id: newDraftId(), formData, status: 'draft', createdAt: Date.now() };
    setDrafts((prev) => [draft, ...prev]);
    saveDraft(draft);
    setIsFormOpen(false);
    return draft;
  };
  // خانه‌ی رویایی: بدونِ فرم، مستقیم طراحیِ دوبعدی
  const startDream = () => {
    const d = handleCreate({ title: DREAM_TITLE, dream: true });
    setEditingDraftId(d.id);
  };
  // بارِ اول که هیچ طرحی نیست: همان لحظه واردِ طراحی شود
  const autoStarted = useRef(false);
  useEffect(() => {
    if (!dream || !loaded || autoStarted.current) return;
    autoStarted.current = true;
    if (!drafts.length) startDream();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dream, loaded]);
  // persist: false فقط برای پیشرفتِ لحظه‌ای (درصدِ ارسال)
  const patchDraft = (id, patch, persist = true) =>
    setDrafts((prev) =>
      prev.map((d) => {
        if (d.id !== id) return d;
        const next = { ...d, ...patch };
        if (persist) saveDraft(next);
        return next;
      })
    );

  const handleDelete = (draft) => {
    if (draft.filmUrl) URL.revokeObjectURL(draft.filmUrl);
    setDrafts((prev) => prev.filter((d) => d.id !== draft.id));
    deleteDraft(draft.id);
    setConfirmDelete(null);
  };

  // پلانِ نیمه‌کاره: هر تغییر در دوبعدی (کمی بعد از آخرین تغییر) و موقعِ خروج
  const handleAutosave = (draftId, editorState) => patchDraft(draftId, { editorState });

  // آدرسِ موقتِ فیلم‌ها با بسته شدنِ صفحه آزاد شود
  const draftsRef = useRef(allDrafts);
  draftsRef.current = allDrafts;
  useEffect(() => () => draftsRef.current.forEach((d) => d.filmUrl && URL.revokeObjectURL(d.filmUrl)), []);

  // «پایان طراحی» در سه‌بعدی: فیلم و فایل‌ها آماده‌اند → برگشت به همین صفحه (هنوز منتشر نشده)
  const handleFinish = (draftId, prepared) => {
    const old = drafts.find((d) => d.id === draftId);
    if (old && old.filmUrl) URL.revokeObjectURL(old.filmUrl);
    const film = preparedFilm(prepared);
    patchDraft(draftId, {
      prepared,
      editorState: prepared.editorState || null,
      filmUrl: film ? URL.createObjectURL(film) : null,
      status: 'designed',
      error: '',
      progress: null,
    });
    setEditingDraftId(null);
  };

  const [authFor, setAuthFor] = useState(null);
  // «انتشار» بدونِ پلان/رندر → اول هشدار: { draft, reason }
  const [noPlanAsk, setNoPlanAsk] = useState(null);
  const requestPublish = (draft) => {
    if (draft.prepared) {
      handlePublish(draft);
      return;
    }
    // خانه‌ی رویایی بدونِ سه‌بعدی معنا ندارد → ادامه‌ی طراحی
    if (dream) {
      setEditingDraftId(draft.id);
      return;
    }
    const drew = draft.editorState && draft.editorState.walls && draft.editorState.walls.length;
    setNoPlanAsk({ draft, reason: drew ? 'render' : 'plan' });
  };

  const handlePublish = async (draft, listingOnly = false) => {
    patchDraft(draft.id, { status: 'publishing', error: '', progress: { sent: 0, total: 0 } }, false);
    try {
      const title = draft.formData.title || `طرحِ ${new Date().toLocaleDateString('fa-IR', { month: 'long', day: 'numeric' })}`;
      // بدونِ سه‌بعدی: فقط اطلاعات و عکس‌های آگهی + تصویرِ پیش‌فرضِ «بدونِ پلانِ سه‌بعدی» (یک بار ساخته و نگه داشته می‌شود)
      let prepared = draft.prepared;
      if (!prepared && listingOnly) {
        prepared = draft.listingPrepared || (await prepareListingOnly(draft.formData, draft.editorState));
        patchDraft(draft.id, { listingPrepared: prepared }, false);
      }
      if (!prepared) throw new Error('پلانِ این فایل هنوز آماده نیست.');
      await publishDesign(prepared, title, (sent, total) => patchDraft(draft.id, { progress: { sent, total } }, false));
      patchDraft(draft.id, { status: 'published', progress: null, listingOnly: !draft.prepared });
    } catch (e) {
      if (e.code === 'not-logged-in') {
        patchDraft(draft.id, { status: draft.prepared ? 'designed' : 'draft', progress: null });
        setAuthFor({ id: draft.id, listingOnly });
        return;
      }
      patchDraft(draft.id, { status: draft.prepared ? 'designed' : 'draft', progress: null, error: e.message || 'انتشار ناموفق بود؛ دوباره امتحان کنید.' });
    }
  };

if (editingDraftId !== null) {
  const editingDraft = drafts.find((d) => d.id === editingDraftId);
  return (
    <CreatePlanEditor
      onClose={() => setEditingDraftId(null)}
      propertyArea={Number(editingDraft?.formData?.area) || null}
      site={siteFromForm(editingDraft?.formData)}
      // کلِ آگهی (متراژ، لوکیشن، قیمت، عکس‌ها …) — با «انتشار» همراهِ طرح ذخیره می‌شود؛ خانه‌ی رویایی آگهی ندارد
      listing={dream ? null : editingDraft?.formData || null}
      // «ویرایش» بعد از «پایان طراحی»: همان پلانِ قبلی (از دوبعدی)
      initialPlan={editingDraft?.editorState || null}
      onFinish={(prepared) => handleFinish(editingDraftId, prepared)}
      onAutosave={(state) => handleAutosave(editingDraftId, state)}
    />
  );
}

  return (
    <div className={`plan-drafts ${dream ? 'is-dream' : ''}`}>
      {dream && (
        <header className="plan-drafts__dream-head">
          <button type="button" onClick={onClose} aria-label="برگشت">
            <ArrowRight size={22} strokeWidth={2} />
          </button>
          <strong>{DREAM_TITLE}</strong>
        </header>
      )}
      <button
        type="button"
        className="plan-drafts__new-btn"
        onClick={() => (dream ? startDream() : setIsFormOpen(true))}
      >
        <Plus size={18} strokeWidth={2.2} />
        <span>{dream ? 'طراحیِ خانه‌ی رویاییِ جدید' : 'ساخت فایل جدید'}</span>
      </button>

      <section className="plan-drafts__list">
        {drafts.map((draft) => (
          <article key={draft.id} className="plan-drafts__card">
            {draft.status !== 'publishing' &&
              (confirmDelete === draft.id ? (
                <div className="plan-drafts__confirm">
                  <span>این فایل حذف شود؟</span>
                  <button type="button" className="is-danger" onClick={() => handleDelete(draft)}>
                    حذف
                  </button>
                  <button type="button" onClick={() => setConfirmDelete(null)}>
                    انصراف
                  </button>
                </div>
              ) : (
                <button type="button" className="plan-drafts__delete" onClick={() => setConfirmDelete(draft.id)} aria-label="حذفِ فایل">
                  <Trash2 size={16} strokeWidth={1.9} />
                </button>
              ))}
            <div className="plan-drafts__info">
              <h3>{draft.formData.title || 'بدون عنوان'}</h3>

              {draft.formData.dealType === 'sale' && draft.formData.price && (
                <p className="plan-drafts__price">
                  {Number(draft.formData.price).toLocaleString('fa-IR')} تومان
                </p>
              )}
              {draft.formData.dealType === 'rent' && (
                <p className="plan-drafts__price">
                  {draft.formData.deposit &&
                    `ودیعه ${Number(draft.formData.deposit).toLocaleString('fa-IR')}`}
                  {draft.formData.rent &&
                    ` - اجاره ${Number(draft.formData.rent).toLocaleString('fa-IR')}`}
                </p>
              )}

              <p className="plan-drafts__meta">
                {draft.formData.area && `${draft.formData.area} متر`}
                {draft.formData.rooms && ` • ${draft.formData.rooms} اتاق`}
              </p>

              {draft.formData.address && (
                <p className="plan-drafts__address">{draft.formData.address}</p>
              )}
            </div>

            <div className="plan-drafts__plan">
              {draft.prepared ? (
                draft.filmUrl ? (
                  <FilmBoxes src={draft.filmUrl} segments={draft.prepared.filmSegments} />
                ) : (
                  <div className="plan-drafts__film is-empty">پلانِ سه‌بعدی آماده است</div>
                )
              ) : (
                <button type="button" className="plan-drafts__plan-empty" onClick={() => setEditingDraftId(draft.id)}>
                  <PenLine size={20} strokeWidth={1.6} />
                  <span>
                    {draft.editorState && draft.editorState.walls && draft.editorState.walls.length
                      ? 'ادامه‌ی کشیدنِ پلان'
                      : 'شروع به کشیدن پلان کنید'}
                  </span>
                </button>
              )}
              {draft.status === 'publishing' ? (
                <div className="plan-drafts__publishing">
                  <div className="plan-drafts__bar">
                    <i style={{ width: `${draft.progress && draft.progress.total ? (draft.progress.sent / draft.progress.total) * 100 : 3}%` }} />
                  </div>
                  <span>
                    {draft.progress && draft.progress.total
                      ? `در حالِ انتشار — ${fa((draft.progress.sent / draft.progress.total) * 100)}٪ (${fa(draft.progress.sent / 1048576, 1)} از ${fa(draft.progress.total / 1048576, 1)} مگابایت)`
                      : 'در حالِ انتشار…'}
                  </span>
                  <small>تا پایانِ انتشار این صفحه را نبندید.</small>
                </div>
              ) : draft.status === 'published' ? (
                <div className="plan-drafts__published">
                  <Check size={18} strokeWidth={2.4} />
                  <span>
                    {draft.listingOnly
                      ? 'منتشر شد (بدونِ سه‌بعدی)'
                      : dream
                        ? 'منتشر شد — در پروفایل و اکسپلور («همه‌ی طراحی‌ها») دیده می‌شود'
                        : 'منتشر شد — در پروفایل و اکسپلور دیده می‌شود'}
                  </span>
                </div>
              ) : (
                <>
                  {draft.error && <p className="plan-drafts__error">{draft.error}</p>}
                  <div className="plan-drafts__actions">
                    <button type="button" className="plan-drafts__edit" onClick={() => setEditingDraftId(draft.id)}>
                      <Pencil size={16} strokeWidth={2} />
                      <span>ویرایش</span>
                    </button>
                    <button type="button" className="plan-drafts__publish" onClick={() => requestPublish(draft)}>
                      <Send size={16} strokeWidth={2} />
                      <span>{draft.error ? 'تلاشِ دوباره' : 'انتشار'}</span>
                    </button>
                  </div>
                </>
              )}
            </div>

            <button
              type="button"
              className={`plan-drafts__status ${draft.status === 'published' ? 'is-published' : draft.prepared ? 'is-pending' : ''}`}
            >
              {draft.status === 'published'
                ? 'منتشر شده'
                : draft.prepared
                  ? 'آماده‌ی انتشار'
                  : dream
                    ? 'طراحی تمام نشده'
                    : 'ثبت تکمیل نشده'}
            </button>
          </article>
        ))}

        {drafts.length === 0 && (
          <p className="plan-drafts__empty">{dream ? 'هنوز خانه‌ای طراحی نکرده‌اید' : 'هنوز فایلی ثبت نکرده‌اید'}</p>
        )}
      </section>

      {authFor !== null && (
        <AuthModal
          onClose={() => setAuthFor(null)}
          onSuccess={() => {
            const d = drafts.find((x) => x.id === authFor.id);
            const only = authFor.listingOnly;
            setAuthFor(null);
            if (d) handlePublish(d, only);
          }}
        />
      )}

      {noPlanAsk && (
        <div className="plan-drafts__ask" onClick={() => setNoPlanAsk(null)}>
          <div className="plan-drafts__ask-box" onClick={(e) => e.stopPropagation()}>
            <strong>این آگهی پلانِ سه‌بعدی ندارد</strong>
            <p>
              {noPlanAsk.reason === 'plan'
                ? 'پلانِ دوبعدیِ این خانه هنوز کشیده نشده و رندرِ سه‌بعدی هم گرفته نشده.'
                : 'پلانِ دوبعدی کشیده شده ولی هنوز سه‌بعدی و رندر («پایانِ طراحی») انجام نشده.'}{' '}
              اگر الان منتشر کنید، فقط اطلاعات و عکس‌های آگهی نمایش داده می‌شود و به‌جای فیلمِ سه‌بعدی یک تصویرِ «بدونِ پلانِ
              سه‌بعدی» می‌آید؛ بازدیدکننده‌ها نمی‌توانند واردِ خانه شوند. بدونِ این کارها منتشر شود؟
            </p>
            <div>
              <button
                type="button"
                className="is-primary"
                onClick={() => {
                  const d = noPlanAsk.draft;
                  setNoPlanAsk(null);
                  handlePublish(d, true);
                }}
              >
                بله، منتشر شود
              </button>
              <button
                type="button"
                onClick={() => {
                  const d = noPlanAsk.draft;
                  setNoPlanAsk(null);
                  setEditingDraftId(d.id);
                }}
              >
                {noPlanAsk.reason === 'plan' ? 'نه، اول پلان را می‌کشم' : 'نه، اول طراحی را تمام می‌کنم'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isFormOpen && (
        <CreatePlanForm onClose={() => setIsFormOpen(false)} onSubmit={handleCreate} />
      )}
    </div>
  );
}

export default PlanDraftsPage;